import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Plus, Search, X, Clock, DollarSign, Users, ArrowRight, FileText, Bookmark } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";
import { confirmDialog } from "../components/ConfirmModal";
import { toast } from "sonner";
import JobDetailModal from "./jobs/JobDetailModal";

const CATEGORIES = [
  { k: "all", label: "All" },
  { k: "creative", label: "Creative" },
  { k: "talent", label: "Talent" },
  { k: "education", label: "Education" },
  { k: "employer", label: "Employer" },
  { k: "other", label: "Other" },
];

const JOB_TYPES = [
  { k: "all", label: "Any Type" },
  { k: "full_time", label: "Full Time" },
  { k: "part_time", label: "Part Time" },
  { k: "freelance", label: "Freelance" },
  { k: "contract", label: "Contract" },
  { k: "gig", label: "Gig" },
];

export default function JobsPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ q: "", category: "all", job_type: "all", remote: false, sort: "recent" });
  const [showPost, setShowPost] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [tab, setTab] = useState("browse");

  const load = useCallback(() => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all" && v !== false && v !== "") params.set(k, v); });
    api.get(`/jobs?${params.toString()}`).then((r) => setJobs(r.data)).finally(() => setBusy(false));
    if (user) {
      api.get("/my/jobs").then((r) => setMyJobs(r.data)).catch(() => {});
      api.get("/my/applications").then((r) => setMyApplications(r.data)).catch(() => {});
    }
  }, [filters, user]);
  useEffect(() => { load(); }, [load]);

  const totalJobs = jobs.length;

  return (
    <div className="pb-32" data-testid="jobs-page">
      <section className="relative grain-bg border-b border-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "linear-gradient(180deg, rgba(5,5,5,0.45) 0%, rgba(5,5,5,0.95) 95%), url('https://images.pexels.com/photos/207662/pexels-photo-207662.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
          <div className="font-mono-label text-[11px] text-[var(--accent)] mb-3">JOBS BOARD</div>
          <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] mb-4">
            Hire the <span className="text-[var(--accent)]">Culture</span>
          </h1>
          <p className="text-neutral-300 max-w-2xl text-lg mb-8">
            Labels, studios, brands & creatives post opportunities. Apply with your Culture Connect profile — no extra forms.
          </p>
          <div className="flex flex-wrap gap-3">
            {user ? (
              <button onClick={() => { setEditing(null); setShowPost(true); }} className="btn-primary" data-testid="post-job-btn">
                <Plus size={14} /> Post a Job
              </button>
            ) : (
              <Link to="/signup" className="btn-primary">Sign Up to Post <ArrowRight size={14} /></Link>
            )}
            <Link to="/culture-connect" className="btn-ghost">Browse Talent <Users size={14} /></Link>
          </div>
        </div>
      </section>

      {/* Tabs */}
      {user && (myJobs.length > 0 || myApplications.length > 0) && (
        <section className="border-b border-neutral-800 bg-[var(--surface)]">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
            {[
              { k: "browse", label: "Browse Jobs", n: totalJobs },
              { k: "posted", label: "My Postings", n: myJobs.length },
              { k: "applied", label: "My Applications", n: myApplications.length },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`font-mono-label text-[11px] px-4 py-4 border-b-2 ${tab === t.k ? "border-[var(--accent)] text-[var(--accent)]" : "border-transparent text-neutral-400 hover:text-white"}`}
                data-testid={`jobs-tab-${t.k}`}
              >
                {t.label.toUpperCase()} <span className="text-neutral-600">· {t.n}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
        {tab === "browse" && (
          <BrowsePane
            jobs={jobs} busy={busy} filters={filters} setFilters={setFilters}
            onOpen={(j) => setViewing(j)} user={user} onApplied={load}
          />
        )}
        {tab === "posted" && (
          <PostedPane
            myJobs={myJobs}
            onEdit={(j) => { setEditing(j); setShowPost(true); }}
            onDeleted={load}
            onView={(j) => setViewing(j)}
          />
        )}
        {tab === "applied" && (
          <AppliedPane apps={myApplications} />
        )}
      </div>

      {showPost && (
        <PostJobModal
          job={editing}
          onClose={(refresh) => { setShowPost(false); setEditing(null); if (refresh) load(); }}
        />
      )}
      {viewing && (
        <JobDetailModal
          jid={viewing.id}
          onClose={(refresh) => { setViewing(null); if (refresh) load(); }}
          isOwner={user && viewing.employer_id === user.id}
          user={user}
        />
      )}
    </div>
  );
}

function BrowsePane({ jobs, busy, filters, setFilters, onOpen, user, onApplied }) {
  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <aside className="space-y-5">
        <div className="font-mono-label text-[10px] text-[var(--accent)]">FILTERS</div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            className="input-brut pl-9"
            placeholder="Title, skill, company…"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            data-testid="jobs-search"
          />
        </div>
        <div>
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">CATEGORY</div>
          <div className="flex flex-wrap gap-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.k}
                onClick={() => setFilters((f) => ({ ...f, category: c.k }))}
                className={`font-mono-label text-[10px] px-3 py-2 border ${filters.category === c.k ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-800 text-neutral-400 hover:text-white"}`}
                data-testid={`jobs-cat-${c.k}`}
              >
                {c.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">TYPE</div>
          <select className="input-brut" value={filters.job_type} onChange={(e) => setFilters((f) => ({ ...f, job_type: e.target.value }))} data-testid="jobs-type">
            {JOB_TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
          <input type="checkbox" checked={filters.remote} onChange={(e) => setFilters((f) => ({ ...f, remote: e.target.checked }))} data-testid="jobs-remote" />
          REMOTE ONLY
        </label>
        <div>
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">SORT</div>
          <select className="input-brut" value={filters.sort} onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}>
            <option value="recent">Most recent</option>
            <option value="budget_high">Budget: high → low</option>
            <option value="budget_low">Budget: low → high</option>
          </select>
        </div>
      </aside>

      <div>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">{jobs.length} {jobs.length === 1 ? "OPENING" : "OPENINGS"}</div>
        {busy ? (
          <p className="font-mono-label text-[10px] text-neutral-500">Loading roles…</p>
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-neutral-800 p-12 text-center" data-testid="jobs-empty">
            <Briefcase size={28} className="mx-auto text-neutral-600 mb-4" />
            <p className="font-mono-label text-[11px] text-neutral-500">No jobs match these filters yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="jobs-list">
            {jobs.map((j) => <JobRow key={j.id} job={j} onOpen={() => onOpen(j)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, onOpen }) {
  return (
    <button onClick={onOpen} className="w-full text-left card-brut p-5 hover:border-[var(--accent)] transition-colors" data-testid={`job-row-${job.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-headline text-xl uppercase">{job.title}</div>
          <div className="font-mono-label text-[10px] text-neutral-400 mt-1">
            {job.employer_company || job.employer_name} · {(job.location || "Remote").toUpperCase()}
          </div>
          <p className="text-neutral-300 text-sm mt-3 line-clamp-2">{job.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="chip">{job.category?.toUpperCase()}</span>
            <span className="chip">{job.job_type?.replace("_", " ").toUpperCase()}</span>
            {job.remote && <span className="chip">REMOTE</span>}
            {(job.budget_min > 0 || job.budget_max > 0) && (
              <span className="chip chip-accent">
                ${(job.budget_min || 0).toLocaleString()}{job.budget_max && job.budget_max !== job.budget_min ? `-$${job.budget_max.toLocaleString()}` : ""}/{job.budget_unit}
              </span>
            )}
            {(job.skills || []).slice(0, 3).map((s) => <span key={s} className="font-mono-label text-[10px] text-neutral-400 border border-neutral-800 px-2 py-1">{s.toUpperCase()}</span>)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono-label text-[10px] text-neutral-500">{job.applicants_count || 0} APPLIED</div>
          <div className="font-mono-label text-[9px] text-neutral-600 mt-1">{new Date(job.created_at).toLocaleDateString()}</div>
        </div>
      </div>
    </button>
  );
}

function PostedPane({ myJobs, onEdit, onDeleted, onView }) {
  const del = async (j) => {
    const ok = await confirmDialog({ title: "Delete posting?", message: `"${j.title}" and ${j.applicants_count || 0} applications will be removed.`, confirmLabel: "Delete" });
    if (!ok) return;
    await api.delete(`/jobs/${j.id}`);
    onDeleted();
  };
  const setStatus = async (j, status) => {
    await api.put(`/jobs/${j.id}/status?status=${status}`);
    onDeleted();
  };
  if (myJobs.length === 0) return <p className="font-mono-label text-[10px] text-neutral-500">You haven&apos;t posted anything yet.</p>;
  return (
    <div className="space-y-3" data-testid="my-jobs-list">
      {myJobs.map((j) => (
        <div key={j.id} className="card-brut p-5" data-testid={`my-job-${j.id}`}>
          <div className="flex items-start justify-between gap-4">
            <button onClick={() => onView(j)} className="text-left flex-1 min-w-0">
              <div className="font-headline text-xl uppercase">{j.title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400 mt-1">
                <span className={`chip ${j.status === "open" ? "chip-accent" : ""}`}>{j.status?.toUpperCase()}</span>
                <span className="ml-2">{j.applicants_count || 0} applicants · {j.views || 0} views</span>
              </div>
            </button>
            <div className="flex flex-wrap gap-1 shrink-0">
              {j.status === "open" && (
                <button onClick={() => setStatus(j, "closed")} className="font-mono-label text-[10px] text-neutral-400 hover:text-white px-2 py-1 border border-neutral-800" data-testid={`close-job-${j.id}`}>CLOSE</button>
              )}
              {j.status !== "open" && (
                <button onClick={() => setStatus(j, "open")} className="font-mono-label text-[10px] text-[var(--accent)] hover:underline px-2 py-1 border border-neutral-800" data-testid={`reopen-job-${j.id}`}>REOPEN</button>
              )}
              <button onClick={() => onEdit(j)} className="font-mono-label text-[10px] text-neutral-400 hover:text-white px-2 py-1 border border-neutral-800" data-testid={`edit-job-${j.id}`}>EDIT</button>
              <button onClick={() => del(j)} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)] px-2 py-1 border border-neutral-800" data-testid={`delete-job-${j.id}`}>DELETE</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AppliedPane({ apps }) {
  if (apps.length === 0) return <p className="font-mono-label text-[10px] text-neutral-500">No applications yet — go apply to a job.</p>;
  return (
    <div className="space-y-3" data-testid="my-apps-list">
      {apps.map((a) => (
        <div key={a.id} className="card-brut p-5" data-testid={`my-app-${a.id}`}>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-headline text-xl uppercase">{a.job_title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400 mt-1">
                <span className={`chip ${a.status === "hired" ? "chip-accent" : ""}`}>{a.status?.toUpperCase()}</span>
                <span className="ml-2">Applied {new Date(a.created_at).toLocaleDateString()}</span>
              </div>
              {a.cover_letter && <p className="text-neutral-300 text-sm mt-3 line-clamp-2">{a.cover_letter}</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PostJobModal({ job, onClose }) {
  const isEdit = !!job;
  const [f, setF] = useState({
    title: "", description: "", category: "creative", skills: [],
    location: "", state: "", remote: false, job_type: "freelance",
    budget_min: 0, budget_max: 0, budget_unit: "project", deadline: "",
    cover_image: "", company: "",
    ...job,
  });
  const [skillInput, setSkillInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const addSkill = () => {
    const v = skillInput.trim();
    if (!v || f.skills.includes(v)) return setSkillInput("");
    setF((s) => ({ ...s, skills: [...s.skills, v] }));
    setSkillInput("");
  };
  const removeSkill = (s) => setF((st) => ({ ...st, skills: st.skills.filter((x) => x !== s) }));
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...f, budget_min: parseFloat(f.budget_min) || 0, budget_max: parseFloat(f.budget_max) || 0 };
      if (isEdit) await api.put(`/jobs/${job.id}`, body);
      else        await api.post("/jobs", body);
      onClose(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };
  const canSave = f.title.trim() && f.description.trim();
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-start justify-center p-4 overflow-y-auto" onClick={() => onClose(false)} data-testid="post-job-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-2xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-headline text-2xl uppercase">{isEdit ? "Edit" : "Post"} Job</h3>
          <button onClick={() => onClose(false)}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <input className="input-brut" placeholder="Job title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="post-title" />
          <input className="input-brut" placeholder="Company / brand (optional)" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="post-company" />
          <textarea rows={5} className="input-brut resize-none" placeholder="Describe the role, deliverables, timeline…" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="post-description" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-brut" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="post-category">
              {CATEGORIES.filter(c => c.k !== "all").map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            </select>
            <select className="input-brut" value={f.job_type} onChange={(e) => setF({ ...f, job_type: e.target.value })} data-testid="post-type">
              {JOB_TYPES.filter(t => t.k !== "all").map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className="input-brut" placeholder="City / State" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="post-location" />
            <input className="input-brut" placeholder="State (e.g. CA)" maxLength={2} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} data-testid="post-state" />
          </div>
          <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={f.remote} onChange={(e) => setF({ ...f, remote: e.target.checked })} data-testid="post-remote" />
            REMOTE FRIENDLY
          </label>
          <div className="grid grid-cols-[1fr_1fr_140px] gap-3">
            <input type="number" className="input-brut" placeholder="Budget min" value={f.budget_min} onChange={(e) => setF({ ...f, budget_min: e.target.value })} data-testid="post-budget-min" />
            <input type="number" className="input-brut" placeholder="Budget max" value={f.budget_max} onChange={(e) => setF({ ...f, budget_max: e.target.value })} data-testid="post-budget-max" />
            <select className="input-brut" value={f.budget_unit} onChange={(e) => setF({ ...f, budget_unit: e.target.value })}>
              <option value="project">per project</option>
              <option value="hour">per hour</option>
              <option value="day">per day</option>
              <option value="year">per year</option>
            </select>
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">SKILLS</div>
            <div className="flex gap-2 mb-2">
              <input className="input-brut" placeholder="Add skill, press +" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} data-testid="post-skill-input" />
              <button type="button" onClick={addSkill} className="btn-ghost shrink-0">+</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {f.skills.map((s) => (
                <span key={s} className="font-mono-label text-[10px] text-neutral-300 border border-neutral-800 px-2 py-1 inline-flex items-center gap-2">
                  {s}
                  <button type="button" onClick={() => removeSkill(s)} className="text-neutral-500 hover:text-[var(--accent)]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
          {err && <p className="text-[var(--accent)] font-mono-label text-[10px]">{err}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy || !canSave} className="btn-primary flex-1 justify-center" data-testid="post-submit">
              {busy ? "Saving…" : isEdit ? "Save Changes" : "Publish Job"}
            </button>
            <button onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
